import { useEffect, useRef } from "react";
import { useGame, type LogEntry } from "../store";
import { OptionalImage } from "./OptionalImage";
import { useReducedMotion } from "../hooks/useReducedMotion";

const OUTCOME_TEXT: Record<number, string> = {
  3: "检定大成功",
  2: "检定成功",
  1: "检定失败",
  0: "检定大失败",
};

function EventThumb() {
  return (
    <div className="event-art-thumb fallback" aria-hidden="true">
      {Array.from({ length: 9 }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

function ItemThumb() {
  return (
    <div className="log-item-icon item-fallback" aria-hidden="true">
      {Array.from({ length: 9 }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

function Entry({ e, isLatestEvent }: { e: LogEntry; isLatestEvent: boolean }) {
  switch (e.kind) {
    case "event":
      return (
        <div className="log-event">
          <div className="event-entry-head">
            {!isLatestEvent && (
              <OptionalImage
                className="event-art-thumb"
                src={e.imageUrl}
                alt=""
                loading="lazy"
                fallback={<EventThumb />}
              />
            )}
            <div>
              <div className="event-kicker">{isLatestEvent ? "当前事件" : "旧章"}</div>
              <h3>{e.name}</h3>
            </div>
          </div>
          <p>{e.des}</p>
        </div>
      );
    case "items":
      return (
        <div className="log-items">
          {e.items.map((it, i) => (
            <div key={i} className={it.gained ? "item-gain" : "item-miss"}>
              <OptionalImage
                src={it.imageUrl}
                alt=""
                className="log-item-icon"
                loading="lazy"
                fallback={<ItemThumb />}
              />
              <span>
                {it.gained ? "＋ 获得藏品" : "✕ 无法获得藏品"}
                <b>【{it.name}】</b>
                {it.gained && it.eff ? <span className="eff"> · {it.eff}</span> : null}
              </span>
            </div>
          ))}
        </div>
      );
    case "challenges":
      return (
        <div className="log-challenges">
          {e.items.map((ch, i) => (
            <div key={i} className="challenge">
              <span className="ch-name">＊挑战【{ch.name}】</span>
              <span className={ch.passed ? "ch-pass" : "ch-fail"}>
                {ch.passed ? "【誓约应验】" : "【誓约破裂】"}
              </span>
              <div className="ch-info">{ch.info}</div>
            </div>
          ))}
        </div>
      );
    case "action":
      return <div className="log-action">» {e.name}</div>;
    case "result": {
      const cls =
        e.rolled.outcome >= 2 ? "win" : e.rolled.outcome <= 1 ? "lose" : "";
      const sum =
        e.rolled.crit === "none"
          ? `（${e.rolled.dice.join(" + ")} ＝ 点数 ${e.rolled.total}，需求 ${e.req}）`
          : "";
      return (
        <div className={`log-result ${cls}`}>
          🎲 {OUTCOME_TEXT[e.rolled.outcome]} {sum}
        </div>
      );
    }
    case "system":
      return <div className="log-system">{e.text}</div>;
  }
}

export function NarrativeLog() {
  const log = useGame((s) => s.log);
  const latestEventId = [...log].reverse().find((e) => e.kind === "event")?.id ?? null;
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: reducedMotion ? "auto" : "smooth",
    });
  }, [log, reducedMotion]);

  return (
    <div className="narrative" ref={ref}>
      {log.map((e) => (
        <Entry key={e.id} e={e} isLatestEvent={e.kind === "event" && e.id === latestEventId} />
      ))}
    </div>
  );
}
